
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Users, UserCheck, UserX, TrendingUp, Clock } from 'lucide-react';
import { StaffManagedUser } from '@/hooks/auth/staffUserManagement';

interface StaffUserManagementStatsProps {
  users: StaffManagedUser[];
  loading: boolean;
  onQuickAction: (action: string) => void;
}

const StaffUserManagementStats: React.FC<StaffUserManagementStatsProps> = ({
  users,
  loading,
  onQuickAction
}) => {
  const stats = React.useMemo(() => {
    const total = users.length;
    const approved = users.filter(u => u.is_approved).length;
    const pending = users.filter(u => !u.is_approved).length;
    const recentUsers = users.filter(u => {
      const created = new Date(u.created_at);
      const dayAgo = new Date();
      dayAgo.setDate(dayAgo.getDate() - 1);
      return created > dayAgo;
    }).length;

    return { total, approved, pending, recentUsers };
  }, [users]);

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        {[1, 2, 3, 4].map(i => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-6">
              <div className="h-8 w-8 bg-muted rounded mb-2"></div>
              <div className="h-6 w-12 bg-muted rounded mb-1"></div>
              <div className="h-4 w-20 bg-muted rounded"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Users</p>
                <p className="text-2xl font-bold text-blue-600">{stats.total}</p>
              </div>
              <Users className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Approved Users</p>
                <p className="text-2xl font-bold text-green-600">{stats.approved}</p>
              </div>
              <UserCheck className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Pending Approval</p>
                <p className="text-2xl font-bold text-orange-600">{stats.pending}</p>
                {stats.pending > 0 && (
                  <Badge variant="outline" className="mt-1 text-orange-600 border-orange-200">
                    Needs Attention
                  </Badge>
                )}
              </div>
              <UserX className="h-8 w-8 text-orange-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">New (24h)</p>
                <p className="text-2xl font-bold text-purple-600">{stats.recentUsers}</p>
              </div>
              <Clock className="h-8 w-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Quick Actions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Button 
              variant="outline" 
              className="justify-start"
              onClick={() => onQuickAction('approve-pending')}
              disabled={stats.pending === 0}
            >
              <UserCheck className="h-4 w-4 mr-2" />
              Approve Pending Users ({stats.pending})
            </Button>
            
            <Button 
              variant="outline" 
              className="justify-start"
              onClick={() => onQuickAction('create-user')}
            >
              <Users className="h-4 w-4 mr-2" />
              Create New User
            </Button>
            
            <Button 
              variant="outline" 
              className="justify-start"
              onClick={() => onQuickAction('bulk-assign')}
            >
              <TrendingUp className="h-4 w-4 mr-2" />
              Bulk Course Assignment
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default StaffUserManagementStats;
